import Image from "@/components/Image";
import Masonry from "@/components/Masonry";
import { getImageSrc2x } from "@/utils/imageUrl";
import { useMemoizedFn } from "ahooks";
import classNames from "classnames";
import { memo, useMemo } from "react";
import HBIcon from "../HBIcon";
import styles from "./index.module.less";

interface IBoardCardAProps {
  id?: string;
  board: IBoardInfo;
  width?: number;
  /**
   * 来源，用于埋点
   */
  viewSource?: string | number;
  /**
   * 当前采集信息（用于在顶部显示预览图）
   */
  currentPin?: IPin;
  /**
   * 位置索引，用于埋点
   */
  location?: number;
  /**
   * 搜索词，用于埋点
   */
  searchWord?: string;
  /**
   * 是否禁用点击
   */
  noClick?: boolean;
}

function BoardCardA({
  id = "BoardCardA",
  board,
  width = 240,
  viewSource,
  currentPin,
  location,
  searchWord,
  noClick,
  ...rest
}: IBoardCardAProps & React.HTMLAttributes<HTMLAnchorElement>) {
  // 取画板的前15个采集作为封面展示
  const renderPins: IPin[] = useMemo(() => {
    if (!board?.pins || board.pins.length === 0) {
      return [];
    }
    return [...board.pins].reverse().slice(0, 15);
  }, [board?.pins]);

  // 分离横图和其他图片（参考 SimilarCardA 逻辑）
  const { horizontalImages, otherImages } = useMemo(() => {
    const horizontalList = renderPins?.filter(
      (item) => item.file.width / item.file.height > 1.1
    );
    const otherList = renderPins?.filter(
      (item) => item.file.width / item.file.height <= 1.1
    );

    return {
      horizontalImages: horizontalList,
      otherImages: otherList,
    };
  }, [renderPins]);

  // 计算列数和列宽
  // content有左右各8px的padding，但实际计算用22px（Masonry内部处理导致）
  // card左右各2px的border(共4px)，两列间gap为2px
  const { columnSize, columnNum } = useMemo(() => {
    const contentPadding = 22; // 实际使用22px（不是24px）以匹配Masonry行为
    const borderWidth = 4; // 左右各2px
    const gutter = 2; // 列间距
    const totalPadding = contentPadding + borderWidth;

    // 筛选出横图数量
    const horizontalList = renderPins.filter(
      (item) => item.file.width / item.file.height > 1.1
    );
    if (
      // 横图超过一半，只展示一列
      horizontalList.length >
      renderPins.length / 2
    ) {
      return {
        columnNum: 1,
        columnSize: Math.floor(width - totalPadding),
      };
    } else {
      return {
        columnNum: 2,
        columnSize: Math.floor((width - totalPadding - gutter) / 2),
      };
    }
  }, [renderPins, width]);

  const render = useMemoizedFn((pin) => {
    // 找到当前 pin 在 otherImages 中的索引
    const index = otherImages.findIndex((p) => p.pin_id === pin.pin_id);

    // 判断是否需要应用圆角
    // 只有在没有横图的情况下，才给 Masonry 的图片添加顶部圆角
    // 如果是两列布局，前两张图分别应用左上和右上圆角
    // 如果是一列布局，第一张图应用左上和右上圆角
    const needsTopLeftRadius = index === 0 && horizontalImages.length === 0;
    const needsTopRightRadius =
      columnNum === 1
        ? needsTopLeftRadius
        : index === 1 && horizontalImages.length === 0;

    let borderRadius = "";
    if (needsTopLeftRadius && needsTopRightRadius) {
      borderRadius = "8px 8px 0 0";
    } else if (needsTopLeftRadius) {
      borderRadius = "8px 0 0 0";
    } else if (needsTopRightRadius) {
      borderRadius = "0 8px 0 0";
    }

    return (
      <div key={pin.pin_id} className={styles.imgContent}>
        <Image
          onLoad={(evt) => {
            const target = evt.target as HTMLElement;
            target?.classList.add("transparent-img-bg");
          }}
          {...getImageSrc2x(pin.file, { width: columnSize })}
          width={columnSize}
          height={Math.round((pin.file?.height / pin.file?.width) * columnSize)}
          alt={pin.raw_text}
          showError={true}
          style={borderRadius ? { borderRadius } : {}}
        />
      </div>
    );
  });

  // 横图渲染函数
  const renderHorizontalImage = useMemoizedFn((pin) => {
    const contentPadding = 22; // 实际使用22px（不是24px）
    const borderWidth = 4; // 左右各2px
    const imageWidth = Math.floor(width - contentPadding - borderWidth);
    return (
      <div key={pin.pin_id} className={styles.horizontalImageContainer}>
        <Image
          onLoad={(evt) => {
            const target = evt.target as HTMLElement;
            target?.classList.add("transparent-img-bg");
          }}
          {...getImageSrc2x(pin.file, { width: imageWidth })}
          width={imageWidth}
          height={Math.round((pin.file?.height / pin.file?.width) * imageWidth)}
          alt={pin.raw_text}
          showError={true}
        />
      </div>
    );
  });

  // 构建链接
  const boardHref = `/boards/${board.board_id}${
    viewSource ? `?viewSource=${viewSource}` : ""
  }`;

  return (
    <a
      href={boardHref}
      target="_blank"
      className={classNames(styles.card)}
      id={id}
      {...rest}
      data-gd-expose="board_expose"
      data-gd-click="board_click"
      data-board-id={board.board_id}
      data-board_location={location}
      data-search-word={searchWord}
      data-dist-code={(board as any)?.custom_recommend ? "人工" : "算法"}
      data-module-name="单个推荐画板"
      {...(noClick
        ? {
            onClick(e) {
              e.preventDefault();
            },
          }
        : {})}
    >
      {/* 顶部信息区域 */}
      {currentPin && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.tag}>采集也在画板</div>
            <div className={styles.title}>
              <HBIcon className={styles.titleIcon} type="dashboard" />
              <span className={styles.titleText}>
                {board.title || "未命名画板"}
              </span>
            </div>
            <div className={styles.subtitle}>
              {board.pin_count || 0} 采集 · {board.follow_count || 0} 关注
            </div>
          </div>
          <div className={styles.headerRight}>
            <Image
              onLoad={(evt) => {
                const target = evt.target as HTMLElement;
                target?.classList.add("transparent-img-bg");
              }}
              {...getImageSrc2x(currentPin.file, { width: 50 })}
              width={50}
              height={50}
              alt={currentPin.raw_text}
              showError={true}
            />
          </div>
        </div>
      )}

      <div className={classNames(styles.content)}>
        {/* 横图容器 */}
        {horizontalImages?.length > 0 && (
          <div className={styles.horizontalImagesContainer}>
            {horizontalImages.map(renderHorizontalImage)}
          </div>
        )}

        {/* 其他图片使用Masonry布局 */}
        {otherImages?.length > 0 && (
          <Masonry
            bricks={otherImages}
            scrollElement={id}
            brickId="pin_id"
            render={render}
            columnNum={columnNum}
            threshold={800}
            gutter={2}
            columnSize={columnSize}
          />
        )}
      </div>

      {/* 如果没有currentPin，则使用原来的底部样式 */}
      {!currentPin && (
        <div className={styles.footer}>
          <div className={styles.footerContent}>
            <div className={styles.title}>{board.title || "未命名画板"}</div>
            <div className={styles.subtitle}>
              {board.pin_count || 0} 采集 · {board.follow_count || 0} 关注
            </div>
          </div>
        </div>
      )}
    </a>
  );
}

export default memo(BoardCardA);
